import type { GameStateData } from '../game/GameState.js';
import { tilePips } from '../game/types.js';
import type { PlayMove } from './MoveEnumerator.js';
import type { AiChoice, MoveScorer } from './types.js';

/**
 * The single-pass tiers of `DifficultyStrategy` (Easy, Medium) plus the
 * no-play fallback. Split out so the strategy file stays an orchestrator.
 */

const EASY_NOISE = 0.5;
const EASY_SCORE_WEIGHT = 10;
/** Tie-break toward the heavier tile when two plays evaluate equal. */
const MEDIUM_PIP_TIE_BREAK = 0.001;

const PASS_CHOICE: AiChoice = { action: { kind: 'pass' }, score: null, depth: 0 };

/**
 * Easy: weighted-greedy on a *thin* scoring function. Fives rewards
 * immediate score and tile heaviness; penalty (Козёл) has no per-play score,
 * so only heaviness counts. Small random noise so two equally-good moves
 * don't always resolve the same way (avoids feeling robotic).
 */
export function pickEasy(moves: readonly PlayMove[], rng: () => number, penalty: boolean): AiChoice {
  const first = moves[0];
  if (first === undefined) return PASS_CHOICE;
  let best = first;
  let bestScore = -Infinity;
  for (const m of moves) {
    const noise = rng() * EASY_NOISE;
    const immediate = penalty ? 0 : m.score * EASY_SCORE_WEIGHT;
    const score = immediate + tilePips(m.tile) + noise;
    if (score > bestScore) {
      bestScore = score;
      best = m;
    }
  }
  return {
    action: { kind: 'play', tile: best.tile, armIndex: best.armIndex },
    score: bestScore,
    depth: 0,
  };
}

/**
 * Medium: full evaluator on every candidate; pick the highest scoring play.
 * 1-ply lookahead is folded into the evaluator (fives: `opponentScoreExposure`
 * simulates the next opponent's best reply; Козёл: the blocking feature).
 * Going deeper than that is the Hard tier's job (SO-IS-MCTS).
 */
export function pickMedium(
  state: GameStateData,
  seat: number,
  moves: readonly PlayMove[],
  scorer: MoveScorer,
): AiChoice {
  const first = moves[0];
  if (first === undefined) return PASS_CHOICE;
  let best = first;
  let bestScore = -Infinity;
  for (const m of moves) {
    const score = scorer.score(state, seat, m);
    const totalScore = score + tilePips(m.tile) * MEDIUM_PIP_TIE_BREAK;
    if (totalScore > bestScore) {
      bestScore = totalScore;
      best = m;
    }
  }
  return {
    action: { kind: 'play', tile: best.tile, armIndex: best.armIndex },
    score: bestScore,
    depth: 1,
  };
}

/**
 * When the seat cannot play, decide between drawing (if boneyard non-empty
 * and the variant allows draws) or passing. The caller re-invokes after the
 * draw to pick the new play if one is now possible.
 */
export function fallback(state: GameStateData): AiChoice {
  if (state.boneyard.length > 0 && state.ruleSet.drawRule !== 'no-draw') {
    return { action: { kind: 'draw' }, score: null, depth: 0 };
  }
  return PASS_CHOICE;
}
